import { cn } from "../cn";

const PALETTE: [string, string][] = [
  ["#7a1f3d", "#ffffff"],
  ["#ffd9df", "#5c0427"],
  ["#96435c", "#ffffff"],
  ["#f3e3e8", "#6b2a40"],
  ["#474043", "#ffffff"],
  ["#ebe0e3", "#302a2d"],
  ["#5c0427", "#ffd9df"],
  ["#fde7c9", "#7a3e0b"],
  ["#dbe7f6", "#1e3a66"],
  ["#d9f0ea", "#0f4d42"],
];

function hash(value: string): number {
  let h = 0;
  for (let i = 0; i < value.length; i += 1) h = (h * 31 + value.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const skip = new Set(["muhammad", "siti"]);
  const p = parts.length > 2 && skip.has(parts[0].toLowerCase()) ? parts.slice(1) : parts;
  return ((p[0]?.[0] ?? "") + (p.length > 1 ? p[p.length - 1][0] : "")).toUpperCase();
}

const sizes = {
  xs: "size-6 text-[10px]",
  sm: "size-8 text-[11px]",
  md: "size-10 text-label-md",
  lg: "size-12 text-label-lg",
  xl: "size-20 text-headline-md",
  "2xl": "size-24 text-headline-lg",
};

export interface AvatarProps {
  name: string;
  /** Stable key for the color (defaults to name). */
  seed?: string;
  src?: string | null;
  size?: keyof typeof sizes;
  className?: string;
  /** Small status dot in the corner. */
  status?: "online" | "confirmed";
  ring?: boolean;
}

export function Avatar({ name, seed, src, size = "md", className, status, ring }: AvatarProps) {
  const [bg, fg] = PALETTE[hash(seed ?? name) % PALETTE.length];
  return (
    <span className={cn("relative inline-flex shrink-0", className)}>
      {src ? (
        <img
          src={src}
          alt={name}
          className={cn("rounded-full object-cover", sizes[size], ring && "ring-2 ring-surface-container-lowest")}
        />
      ) : (
        <span
          aria-label={name}
          role="img"
          className={cn(
            "inline-flex items-center justify-center rounded-full font-bold tracking-wide select-none",
            sizes[size],
            ring && "ring-2 ring-surface-container-lowest",
          )}
          style={{ backgroundColor: bg, color: fg }}
        >
          {initialsOf(name)}
        </span>
      )}
      {status && (
        <span
          className={cn(
            "absolute right-0 bottom-0 block size-3 rounded-full ring-2 ring-surface-container-lowest",
            status === "online" ? "bg-completed" : "bg-emerald-500",
          )}
          aria-hidden
        />
      )}
    </span>
  );
}
